/** Anlegen- und Bearbeiten-Dialog für Produkte. Zustand bleibt in der Seite. */
import { num } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PriceSchedule } from "@/components/PriceSchedule";
import { TaxPreview } from "@/components/orders/TaxCaseParts";
import { Field, PriceField, SupplierSelect, TaxSelect, DimFields, WeightFields, SurchargeEditor } from "./ProductFields";
import { VariantPriceLists } from "./VariantPriceLists";
import { VariantSalesPromotions } from "./VariantSalesPromotions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Check = ({ obj, setObj, name, label, testId }) => (
  <label className="col-span-2 flex items-center gap-2 rounded-md bg-muted/40 p-2 cursor-pointer">
    <input type="checkbox" checked={!!obj[name]} data-testid={testId}
      onChange={(e) => setObj({ ...obj, [name]: e.target.checked })} />
    <span className="text-sm">{label}</span>
  </label>
);

export const ProductCreateDialog = ({ open, onOpenChange, f, setF, suppliers, taxRates, onSave }) => {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{t("prod.new")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU *" name="sku" obj={f} setObj={setF} />
          <Field label={`EAN (${t("common.optional")})`} name="ean" obj={f} setObj={setF} />
          <div className="col-span-2"><Field label={`${t("common.name")} *`} name="name" obj={f} setObj={setF} /></div>
          <Check obj={f} setObj={setF} name="is_digital" label={t("prod.isDigital")} testId="fld-is_digital-new" />
          <SupplierSelect obj={f} setObj={setF} suppliers={suppliers} /><div />
          {!f.is_digital && (<>
            <Field label={t("prod.unitsPerCarton")} name="units_per_carton" obj={f} setObj={setF} type="number" /><div />
            <DimFields obj={f} setObj={setF} />
            <WeightFields obj={f} setObj={setF} />
            <Field label={t("prod.hsCodeField")} name="hs_code" obj={f} setObj={setF} />
            <Field label={t("prod.htsUs")} name="hts_code_us" obj={f} setObj={setF} />
            <Field label={t("prod.originCountry")} name="country_of_origin" obj={f} setObj={setF} /><div />
            <Check obj={f} setObj={setF} name="strap_option" label={t("prod.strapOption")} testId="fld-strap-new" />
            <Check obj={f} setObj={setF} name="expiry_tracked" label={t("wg.expiryTracked")} testId="fld-expiry-new" />
          </>)}
          <Field label={t("prod.costBase")} name="cost_price_base" obj={f} setObj={setF} type="number" />
          <Field label={t("prod.reorderPoint")} name="reorder_point" obj={f} setObj={setF} type="number" />
          <div className="col-span-2 rounded-md border border-border p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label={t("prod.initialStock")} name="initial_stock" obj={f} setObj={setF} type="number" />
              <Field label={`${t("ex.supplierLot")} (${t("common.optional")})`} name="initial_lot" obj={f} setObj={setF} />
              <Field label={`${t("ex.bbd")} (${t("common.optional")})`} name="initial_bbd" obj={f} setObj={setF} type="date" />
            </div>
            <p className="mt-2 text-xs text-muted-foreground" data-testid="initial-stock-hint">{t("prod.initialStockHint")}</p>
          </div>
          <SurchargeEditor obj={f} setObj={setF} />
          <TaxSelect obj={f} setObj={setF} taxRates={taxRates} /><div />
          <PriceField label={t("common.priceNet")} name="price_net" obj={f} setObj={setF} />
          <PriceField label={t("common.priceGross")} name="price_gross" obj={f} setObj={setF} />
          <PriceField label={`${t("prod.secondPrice")} (${t("common.optional")})`} name="price_net_second" obj={f} setObj={setF} />
          <PriceField label={t("prod.secondPriceGross")} name="price_gross_second" obj={f} setObj={setF} />
        </div>
        <DialogFooter>
          <Button data-testid="save-product-btn" onClick={onSave} disabled={!f.sku || !f.name}>{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ProductEditDialog = ({ edit, setEdit, suppliers, taxRates, onSave, onPricesApplied }) => {
  const { t } = useI18n();
  const cartonCalc = () => {
    const vpe = +edit.units_per_carton;
    const ek = +edit.carton_ek;
    if (!vpe) { toast.error(t("prod.needVpe")); return; }
    if (!ek) { toast.error(t("prod.needCartonCost")); return; }
    const base = Math.round(ek / vpe * 100) / 100;
    setEdit({ ...edit, cost_price_base: String(base) });
    toast.success(`${t("prod.costBase")} = ${num(base)}`);
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{t("prod.edit")}</DialogTitle></DialogHeader>
        {/* Layout: Stammdaten → B2B-Preislisten → Tabs (Preise | Steuer) */}
        {edit && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label={t("common.name")} name="product_name" obj={edit} setObj={setEdit} /></div>
              <Field label="SKU" name="sku" obj={edit} setObj={setEdit} />
              <Field label="EAN" name="ean" obj={edit} setObj={setEdit} />
              <Check obj={edit} setObj={setEdit} name="is_digital" label={t("prod.isDigital")} testId="fld-is_digital" />
              <SupplierSelect obj={edit} setObj={setEdit} suppliers={suppliers} /><div />
              {!edit.is_digital && (<>
                <DimFields obj={edit} setObj={setEdit} />
                <WeightFields obj={edit} setObj={setEdit} />
                <Check obj={edit} setObj={setEdit} name="strap_option" label={t("prod.strapOption")} testId="fld-strap" />
                <Check obj={edit} setObj={setEdit} name="expiry_tracked" label={t("wg.expiryTracked")} testId="fld-expiry" />
              </>)}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <VariantPriceLists variantId={edit.vid} />
              <VariantSalesPromotions variantId={edit.vid} />
            </div>

            <Tabs defaultValue="prices">
              <TabsList className="w-full" data-testid="product-edit-tabs">
                <TabsTrigger value="prices" className="flex-1" data-testid="tab-prices">{t("prod.tabPrices")}</TabsTrigger>
                <TabsTrigger value="tax" className="flex-1" data-testid="tab-tax">{t("prod.tabTax")}</TabsTrigger>
              </TabsList>

              <TabsContent value="prices" className="mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("prod.costBase")} name="cost_price_base" obj={edit} setObj={setEdit} type="number" />
                  {!edit.is_digital && (<>
                    <Field label={t("prod.unitsPerCarton")} name="units_per_carton" obj={edit} setObj={setEdit} type="number" />
                    <Field label={t("prod.reorderPoint")} name="reorder_point" obj={edit} setObj={setEdit} type="number" /><div />
                    <div className="col-span-2 flex items-end gap-2 rounded-md bg-muted/40 p-2">
                      <div className="flex-1"><Label className="text-xs">{t("prod.cartonCost")}</Label>
                        <NumberInput decimals={2} value={edit.carton_ek ?? ""} className="mt-1.5 h-9" testId="carton-ek"
                          onChange={(val) => setEdit({ ...edit, carton_ek: val })} /></div>
                      <Button type="button" variant="outline" size="sm" data-testid="carton-calc-btn" onClick={cartonCalc}>
                        {t("prod.calcBase")}
                      </Button>
                    </div>
                  </>)}
                  <PriceField label={t("common.priceNet")} name="price_net" obj={edit} setObj={setEdit} />
                  <PriceField label={t("common.priceGross")} name="price_gross" obj={edit} setObj={setEdit} />
                  <PriceField label={`${t("prod.secondPrice")} (${t("common.optional")})`} name="price_net_second" obj={edit} setObj={setEdit} />
                  <PriceField label={t("prod.secondPriceGross")} name="price_gross_second" obj={edit} setObj={setEdit} />
                  <SurchargeEditor obj={edit} setObj={setEdit} />
                  <PriceSchedule entityType="variant" entityId={edit.vid} taxRate={+edit.tax_rate || 19}
                    taxRates={taxRates} onApplied={onPricesApplied} />
                </div>
              </TabsContent>

              <TabsContent value="tax" className="mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <TaxSelect obj={edit} setObj={setEdit} taxRates={taxRates} /><div />
                  {!edit.is_digital && (<>
                    <Field label={t("prod.hsCodeField")} name="hs_code" obj={edit} setObj={setEdit} />
                    <Field label={t("prod.htsUs")} name="hts_code_us" obj={edit} setObj={setEdit} />
                    <Field label={t("prod.originCountry")} name="country_of_origin" obj={edit} setObj={setEdit} /><div />
                  </>)}
                  <TaxPreview entityType="variant" entityId={edit.vid} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
        <DialogFooter><Button data-testid="save-edit-btn" onClick={onSave}>{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
